import { DeleteOutlined, EditOutlined, SyncOutlined, CommentOutlined } from '@ant-design/icons';
import { Button, Dropdown, message } from 'antd';
import React, { useState } from 'react';
import styled from 'styled-components';
import Cookies from 'js-cookie';

import { UpdateGovernanceStatusModal } from '../../shared/EntityDropdown/UpdateGovernanceStatusModal';
import { MenuIcon } from '../../shared/EntityDropdown/EntityDropdown';
import { useEntityRegistry } from '../../../useEntityRegistry';
import { CLIENT_AUTH_COOKIE } from '../../../../conf/Global';
import EditDataProductModal from './EditDataProductModal';
import { showDeleteModal } from '../../shared/utils';
import { PreviewType } from '../../Entity';

import { useDeleteDataProductMutation } from '../../../../graphql/dataProduct.generated';
import { useIsEntityAttachedToReadOnlyDatasetsQuery } from '../../../../graphql/entity.generated';
import { useCheckModuleEnabledQuery } from '../../../../graphql/app.generated';
import { useGetCommentsQuery } from '../../../../graphql/user.generated';
import { DataProduct, EntityType } from '../../../../types.generated';
import { postMessageAction, postMessageService } from '../../../shared/postMessageService';
import { useTranslation } from '../../../../i18n/useTranslation';
import { checkEntityAttachedToReadOnlyDatasets } from '../../../shared/deleteUtils';

const ResultWrapper = styled.div`
    margin: 0;
    width: 100%;
    padding: 12px;
    max-width: 100%;
    border-radius: 0px;
    background-color: white;
`;

const StyledButton = styled(Button)`
    background: transparent !important;
    box-shadow: none;
    outline: none;
    border: none;
    height: 18px;
    width: 18px;
    padding: 0;

    svg {
        height: 14px;
        width: 14px;
    }
`;

const ButtonsWrapper = styled.div`
    display: flex;
    justify-content: flex-end;
`;

const StyledMenuIcon = styled(MenuIcon)`
    margin: 0;
`;

const MenuItem = styled.div``;

interface Props {
    dataProduct: DataProduct;
    refetch: () => void;
    onUpdateDataProduct: (dataProduct: DataProduct) => void;
    setDeletedDataProductUrns: React.Dispatch<React.SetStateAction<string[]>>;
}

export default function DataProductResult({
    dataProduct,
    onUpdateDataProduct,
    setDeletedDataProductUrns,
    refetch,
}: Props) {
    const { t } = useTranslation();
    const entityRegistry = useEntityRegistry();
    const { urn } = dataProduct;
    const [isEditModalVisible, setIsEditModalVisible] = useState(false);
    const [isGovernanceModalVisible, setIsGovernanceModalVisible] = useState(false);
    const [deleteDataProductMutation] = useDeleteDataProductMutation();
    const { refetch: checkIsEntityAttachedToReadOnlyDatasets } = useIsEntityAttachedToReadOnlyDatasetsQuery({
        variables: { urn },
        skip: true,
    });

    const { data } = useGetCommentsQuery({
        variables: { input: { entity: urn } },
        skip: !urn, // Skip the query if urn is null or undefined
    });
    const { data: moduleInfo } = useCheckModuleEnabledQuery({ variables: {} });

    const isMessageCenterEnabled = moduleInfo?.checkModuleEnabled?.isEnabled;

    const actorUrn = Cookies.get(CLIENT_AUTH_COOKIE) || '';

    // This method checks if current user has commented in the dataset or not
    const hasUserCommented = () => {
        return data?.getComments?.users?.includes(actorUrn);
    };

    const handleButtonClick = () => {
        postMessageService.sendToParent({
            action: postMessageAction.OPEN_NAVIGATION_MODAL,
            urn,
            isTempChannel: !hasUserCommented(),
        });
    };

    async function deleteDataProduct() {
        const canDelete = await checkEntityAttachedToReadOnlyDatasets({
            entityRegistry,
            entityType: EntityType.DataProduct,
            entityData: dataProduct,
            checkFunction: checkIsEntityAttachedToReadOnlyDatasets,
            modalClassName: 'f-borderless-modal',
            getModalTitle: (displayName: string) => t('Cannot Delete {{name}}', { name: displayName }),
            getModalContent: (entityName: string) =>
                t('This {{entityName}} is attached to read-only datasets and cannot be deleted.', {
                    entityName: t(entityName || ''),
                }),
            getModalOkText: () => t('Ok'),
        });

        if (!canDelete) {
            return;
        }

        deleteDataProductMutation({ variables: { urn } })
            .then(() => {
                message.success(t('Deleted Data Product'));
                setDeletedDataProductUrns((currentUrns) => [...currentUrns, urn]);
            })
            .catch(() => {
                message.destroy();
                message.error({ content: t('Failed to delete Data Product. An unexpected error occurred') });
            });
    }

    function onRemove() {
        showDeleteModal(entityRegistry.getDisplayName(EntityType.DataProduct, dataProduct), deleteDataProduct);
    }

    const items = [
        {
            key: '0',
            label: (
                <MenuItem aria-label="Delete" onClick={onRemove}>
                    <DeleteOutlined /> &nbsp;{t('Delete')}
                </MenuItem>
            ),
        },
        {
            key: '1',
            label: (
                <MenuItem aria-label="Update Governance Status" onClick={() => setIsGovernanceModalVisible(true)}>
                    <SyncOutlined /> &nbsp;{t('Update Governance Status')}
                </MenuItem>
            ),
        },
        ...(isMessageCenterEnabled
            ? [
                  {
                      key: '2',
                      label: (
                          <MenuItem aria-label="Comment" onClick={handleButtonClick}>
                              <CommentOutlined /> &nbsp;{t('Comment')}
                          </MenuItem>
                      ),
                  },
              ]
            : []),
    ];

    return (
        <>
            <ResultWrapper className="f-bottom-border">
                <ButtonsWrapper>
                    <StyledButton
                        type="text"
                        icon={<EditOutlined />}
                        className="icon-button ma-0"
                        onClick={() => setIsEditModalVisible(true)}
                    />
                    <Dropdown menu={{ items }} trigger={['click']}>
                        <StyledButton type="text" className="icon-button ml-2" icon={<StyledMenuIcon />} />
                    </Dropdown>
                </ButtonsWrapper>
                <div>{entityRegistry.renderPreview(EntityType.DataProduct, PreviewType.SEARCH, dataProduct)}</div>
            </ResultWrapper>
            {isEditModalVisible && (
                <EditDataProductModal
                    dataProduct={dataProduct}
                    onClose={() => setIsEditModalVisible(false)}
                    onUpdateDataProduct={onUpdateDataProduct}
                />
            )}

            {isGovernanceModalVisible && (
                <UpdateGovernanceStatusModal
                    entityData={dataProduct}
                    refetch={refetch}
                    onClose={() => setIsGovernanceModalVisible(false)}
                />
            )}
        </>
    );
}
